import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import { IReducer } from "../../state/reducer";
import { Check, X, AlertTriangle } from "react-feather";
import { meetsCostRequirementsBrickPack } from "../../state/BrickActions/Actions/_CostsAndOutcomes/BrickPacks/meetsCostRequirementsBrickPack";
import BrickPackDetails from "../BrickPackDetails/BrickPackDetails";
import "./BrickPackRow.css";
import { meetsCostRequirementsSingleBrickPack } from "../../state/BrickActions/Actions/_CostsAndOutcomes/BrickPacks/meetsCostRequirementsSingleBrickPack";
import { BrickInfoTotal } from "../../state/BrickInfoTotal/BrickInfoTotal";
import { ActionChange } from "../../state/BrickActions/ActionInfo/ActionInfoTypes";

const options = [
  { value: "1", label: "x1" },
  { value: "5", label: "x5" },
  { value: "10", label: "x10" },
  { value: "20", label: "x20" },
  { value: "25", label: "x25" },
  { value: "50", label: "x50" },
  { value: "75", label: "x75" },
  { value: "100", label: "x100" },
  { value: "150", label: "x150" },
  { value: "200", label: "x200" },
];

interface BrickPackRowProps extends IReducer {
  id: string;
  title: string;
  type:
    | "Collect"
    | "Create"
    | "Split"
    | "Capacity"
    | "Auto Collect"
    | "Upgrade"
    | "BuySet"
    | "BrickPack";
  details: string | null;
}

const BrickPackRow: React.FC<BrickPackRowProps> = ({
  id,
  title,
  type,
  details,
  state,
  dispatch,
}) => {
  // Ignore default action
  if (id === "default_action") return null;

  const [showDetails, setShowDetails] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showCapacityTooltip, setShowCapacityTooltip] = useState(false);

  // Get current buy amount from state (default to 1 if not set)
  const currentBuyAmount = state.brickPackBuyAmounts.get(id) ?? 1;
  const selectedOption =
    options.find((opt) => Number(opt.value) === currentBuyAmount) ?? options[0];
  const [popupPosition, setPopupPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  }>({
    left: 0,
  });
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });
  const buttonRef = useRef<HTMLDivElement>(null);
  const capacityIconRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (showPopup && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      // Estimate popup height (base height + costs)
      const action = state.actions.get(id);
      const estimatedPopupHeight = action?.cost.length
        ? 60 + action.cost.length * 30 // Base + costs
        : 100; // Default fallback

      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldPositionAbove = spaceBelow < estimatedPopupHeight + 20; // 20px buffer

      if (shouldPositionAbove) {
        // Position above: use bottom instead of top (no height calculation needed)
        setPopupPosition({
          bottom: window.innerHeight - rect.top + 5,
          left: rect.left,
        });
      } else {
        // Position below the button with 5px margin
        setPopupPosition({
          top: rect.bottom + 5,
          left: rect.left,
        });
      }
    }
  }, [showPopup, id, state.actions]);

  useEffect(() => {
    if (showCapacityTooltip && capacityIconRef.current) {
      const rect = capacityIconRef.current.getBoundingClientRect();

      // Position tooltip above the icon with a small offset
      setTooltipPosition({
        top: rect.top - 35, // 35px above the icon
        left: rect.left + rect.width / 2, // Center horizontally on the icon
      });
    }
  }, [showCapacityTooltip]);

  const typeClass = `actionType${type.charAt(0).toUpperCase() + type.slice(1)}`;

  // Check if ALL bricks in the pack are at max capacity
  const allBricksAtMaxCapacity = () => {
    const action = state.actions.get(id);
    if (!action || !action.outcome.length) return false;

    // Check every brick in the pack's outcome
    return action.outcome.every((outcome) => {
      const brick = state.bricks.get(outcome.brickId);
      if (!brick) return false;

      // If brick hasn't been discovered yet (amount is NaN), it's not at max capacity
      if (isNaN(brick.amount)) return false;

      // Check if brick is at or above capacity
      return brick.amount >= brick.capacity;
    });
  };

  // Check if buy button should be enabled (green checkmark only)
  const canPurchase = () => {
    const buyAmount = Number(selectedOption.value) ?? 1;

    const costsMet = meetsCostRequirementsBrickPack(
      state.actions.get(id)!,
      state.bricks,
      state.sets,
      buyAmount,
    );

    // Can only purchase if costs are met AND not all bricks at max capacity
    return costsMet && !allBricksAtMaxCapacity();
  };

  const renderRequirementsMet = () => {
    const iconSize = 36;
    const buyAmount = Number(selectedOption.value) ?? 1;

    const costsMet = meetsCostRequirementsBrickPack(
      state.actions.get(id)!,
      state.bricks,
      state.sets,
      buyAmount,
    );

    // Red X takes precedence if costs not met
    if (!costsMet) {
      return (
        <X size={iconSize} className={"headerIconBuyRow missingCostIcon"} />
      );
    }

    // Yellow triangle if costs met but all bricks at max capacity
    if (allBricksAtMaxCapacity()) {
      return (
        <AlertTriangle
          ref={capacityIconRef}
          size={iconSize}
          className={"headerIconBuyRow lacksCapacityIcon"}
          onMouseEnter={() => setShowCapacityTooltip(true)}
          onMouseLeave={() => setShowCapacityTooltip(false)}
        />
      );
    }

    // Green checkmark if costs met and not all bricks at max capacity
    return <Check size={iconSize} className={"headerIconBuyRow metCostIcon"} />;
  };

  const renderCosts = (index: number, cost: ActionChange) => {
    const buyAmount = Number(selectedOption.value) ?? 1;
    const trueCost = cost.baseChangeValue * buyAmount;

    // Default to "brick" if type not specified (backward compatibility)
    const costType = cost.type || "brick";

    let currentAmount = 0;
    let itemName = "";

    if (costType === "brick") {
      currentAmount = state.bricks.get(cost.brickId)?.amount ?? 0;
      currentAmount = isNaN(currentAmount) ? 0 : currentAmount;
      itemName = BrickInfoTotal.get(cost.brickId)?.name ?? "Unknown Brick";
    } else if (costType === "set") {
      currentAmount = state.sets.get(cost.brickId)?.count ?? 0;
      itemName = state.sets.get(cost.brickId)?.name ?? "Unknown Set";
    }

    if (
      meetsCostRequirementsSingleBrickPack(
        cost,
        state.bricks,
        state.sets,
        buyAmount,
      )
    ) {
      return (
        <div className="popupCosts" key={index}>
          <Check size={20} className="costIcon metCostIcon" />
          {itemName} ({currentAmount}/{trueCost})
        </div>
      );
    } else {
      return (
        <div className="popupCosts" key={index}>
          <X size={20} className="costIcon missingCostIcon" />
          {itemName} ({currentAmount}/{trueCost})
        </div>
      );
    }
  };

  const executeQuickBuy = () => {
    dispatch({
      type: "buy_brick_pack",
      brickPackId: id,
      buyAmount: Number(selectedOption.value) ?? 1,
    });
  };

  return (
    <>
      <div
        ref={buttonRef}
        className={`noSelect brickPackRowContainer ${typeClass}`}
        onMouseEnter={() => setShowPopup(true)}
        onMouseLeave={() => setShowPopup(false)}
      >
        <div className="brickPackRowHeader">
          <div
            className="brickPackIcon"
            onClick={() => setShowDetails(true)}
          ></div>
          <div
            className="brickPackRowTitle"
            onClick={() => setShowDetails(true)}
          >
            <div className="packTitleText">{title}</div>
          </div>
          <div className="buyPacks">
            <Select
              value={selectedOption}
              onChange={(option) => {
                if (option) {
                  dispatch({
                    type: "SET_BRICK_PACK_BUY_AMOUNT",
                    brickPackId: id,
                    brickPackBuyAmount: Number(option.value),
                  });
                }
              }}
              options={options}
              isSearchable={false}
              styles={{
                control: (provided) => ({
                  ...provided,
                  minWidth: "100px",
                }),
              }}
            />
            {renderRequirementsMet()}
            <div
              className={`buyButton ${!canPurchase() ? "disabled" : ""}`}
              onClick={() => {
                if (canPurchase()) {
                  executeQuickBuy();
                }
              }}
            >
              Buy
            </div>
          </div>
        </div>
      </div>
      <BrickPackDetails
        packId={id}
        packTitle={title}
        state={state}
        dispatch={dispatch}
        isOpen={showDetails}
        onClose={setShowDetails}
      />
      {showPopup &&
        createPortal(
          <div
            className="brickPackPopup"
            style={{
              top: popupPosition.top,
              bottom: popupPosition.bottom,
              left: popupPosition.left,
            }}
          >
            {state.actions.get(id)?.cost.length ? (
              <>
                <b className="costsHeader">Costs: </b>
                <div className="costContent">
                  {state.actions
                    .get(id)
                    ?.cost.map((cost, index) => renderCosts(index, cost))}
                </div>
              </>
            ) : null}
          </div>,
          document.body,
        )}
      {showCapacityTooltip &&
        createPortal(
          <div
            className="capacityTooltip"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
            }}
          >
            No available capacity
          </div>,
          document.body,
        )}
    </>
  );
};

export default BrickPackRow;
